import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardBody, VStack, HStack, Heading, Text, Spinner } from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { ShowcaseView } from '../AdShowcase/ShowcaseView';
import type { ShowcaseAd, ShowcaseCfg, ShowcaseProduct } from '../AdShowcase/types';

type Snapshot = {
  version: number;
  frozenAt: string | null;
  title: string;
  cfg: ShowcaseCfg;
  overrides: Record<string, string>;
  view: 'rows' | 'wall';
  surround: 'dark' | 'light';
  products: ShowcaseProduct[];
  ads: ShowcaseAd[];
};

type ByTokenResponse = {
  snapshot: Snapshot;
  title: string;
};

export function AdShowcasePublicPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setLoading(false); setLoadErr('Missing showcase token'); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiJson<ByTokenResponse>(
          `/api/ad-showcases/by-token/${encodeURIComponent(token)}`
        );
        if (!cancelled) setSnapshot(data.snapshot);
      } catch (e) {
        if (!cancelled) setLoadErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading || loadErr || !snapshot) {
    return (
      <Box minH="100vh" bg="brand.canvas" py={20} px={4}>
        <Box maxW="560px" mx="auto">
          <Card>
            <CardBody p={8}>
              <VStack align="stretch" spacing={5}>
                <Box>
                  <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="rsViolet.500" mb={2}>
                    Ad Showcase
                  </Text>
                  {loading && (
                    <HStack spacing={3} py={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="brand.muted">Loading showcase…</Text>
                    </HStack>
                  )}
                  {loadErr && (
                    <>
                      <Heading size="md" color="brand.ink" mb={2}>Couldn't load showcase</Heading>
                      <Text color="red.600" fontSize="sm">{loadErr}</Text>
                      <Text fontSize="xs" color="brand.muted" mt={3}>
                        If this link was revoked or typed incorrectly, ask the sender for a new one.
                      </Text>
                    </>
                  )}
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh">
      <ShowcaseView
        isSnapshot
        canConfigure={false}
        title={snapshot.title}
        cfg={snapshot.cfg}
        overrides={snapshot.overrides || {}}
        view={snapshot.view === 'wall' ? 'wall' : 'rows'}
        surround={snapshot.surround === 'light' ? 'light' : 'dark'}
        products={snapshot.products || []}
        ads={snapshot.ads || []}
        frozenAt={snapshot.frozenAt}
      />
    </Box>
  );
}

export default AdShowcasePublicPage;
